import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { AvailableFeatures } from '@/api/user/resources/available_feature';
import { key } from '@/store/index';

const useAvailableFeatureCheck = () => {
  const store = useStore(key);
  const availableFeatures = ref<AvailableFeatures>();
  /**
   * Vuex user.salesforceSetting が読み込まれている必要あり
   */
  const salesforceSetting = computed(() => store.getters['user/salesforceSetting']);
  /**
   * Vuex user.client が読み込まれている必要あり
   */
  const client = computed(() => store.getters['user/client']);

  const isAvailableFeature = (key: keyof AvailableFeatures['feature']) => {
    return !!availableFeatures.value?.feature?.[key];
  };

  const fetchAvailableFeaturesAction = async () : Promise<void> => {
    await store.dispatch('user/getAvailableFeaturesAction');
    availableFeatures.value = store.getters['user/availableFeatures'];
  };
  const promiseOfFetchAvailableFeatures = fetchAvailableFeaturesAction();

  const isSlugContained = (slugs: string[]) => slugs.includes(client.value?.slug || '');


  const isAvailableBulkMailFeature = computed<boolean>(() => isAvailableFeature('bulk_mail'));
  const isAvailableDirectMailFeature = computed<boolean>(() => isAvailableFeature('direct_mail'));
  const isAvailableIntercomFeature = computed<boolean>(() => isAvailableFeature('intercom'));
  const isAvailableSalesforceLinkFeature = computed<boolean>(() => isAvailableFeature('salesforce_link'));
  const isAvailableAnalyticsLinkFeature = computed<boolean>(() => isAvailableFeature('analytics'));
  const isAvailableMailTemplateFeature = computed<boolean>(() => isAvailableFeature('mail_template'));
  const isAvailableSalesforceApiFeature = computed<boolean>(() => isAvailableSalesforceLinkFeature.value && salesforceSetting.value?.connectStatus === 'connected');
  const isAvailableAiFeatureToBeReleased = computed<boolean>(() => false);
  const isAvailableBetaPage = computed<boolean>(() => availableFeatures.value?.betaFeature && Object.values(availableFeatures.value.betaFeature).includes(true));
  const isAvailableSequenceFeature = computed<boolean>(() => isAvailableFeature('sequence'));
  const isAvailableTranscriptionFeature = computed<boolean>(() => isAvailableFeature('transcription'));

  const developmentSlugs = ['debug-1', 'test1', 'sc-debug'];
  const isAvailableTriggerFeature = computed<boolean>(() => isSlugContained(developmentSlugs.concat(['balescloud-001', 'demo-005', 'salesforce-test', 'recruit001', 'can-ly', 'akashika-jisho', 'akashika-jisho-002', 'canon-mj', 'principle-c', 'mochica', 'neo-career-doc'])));
  const isAvailableMfaFeature = computed<boolean>(() => isSlugContained(developmentSlugs.concat(['demo-005', 'nttprint'])));
  const isAvailableCockpitNextActionSuggestionsFeature = computed<boolean>(() => isSlugContained(developmentSlugs.concat(['kaonavi001', 'balescloud-001', 'mixi', 'kyocera', 'corp-obc', 'corp-obc002'])));
  const isAvailableCallFeedbackFeature = computed<boolean>(() => isSlugContained(developmentSlugs.concat(['corp-obc', 'corp-obc002', 'kyocera', 'kaonavi001'])));
  const isJinjer = computed<boolean>(() => client.value?.slug === 'jinjer');
  const isSfDevelopment = computed<boolean>(() => isSlugContained(developmentSlugs.concat(['balescloud-001', 'akashika-jisho', 'akashika-jisho-002', 'akashika-jisho-002', 'canon-mj', 'principle-c', 'mochica', 'neo-career-doc'])));

  return {
    promiseOfFetchAvailableFeatures,
    isAvailableBulkMailFeature,
    isAvailableDirectMailFeature,
    isAvailableIntercomFeature,
    isAvailableSalesforceLinkFeature,
    isAvailableAnalyticsLinkFeature,
    isAvailableMailTemplateFeature,
    isAvailableSalesforceApiFeature,
    isAvailableBetaPage,
    isAvailableSequenceFeature,
    isAvailableTranscriptionFeature,
    isAvailableTriggerFeature,
    isAvailableMfaFeature,
    isAvailableAiFeatureToBeReleased,
    isAvailableCockpitNextActionSuggestionsFeature,
    isAvailableCallFeedbackFeature,
    // TODO: 直近の導入のための一時的な処置で使用。不要になり次第消す
    isJinjer,
    isSfDevelopment,
  };
};

export {
  useAvailableFeatureCheck,
};
